export default class Histogram {
	constructor() {
		this.histCanvas = document.getElementById("histogram-canvas");
		this.histCtx = this.histCanvas.getContext("2d");

		this.sourceCtx = null;
		this.imgData = null;
		this.gradients = null;

		this.colors = {
			red: ["#000", "#f00"],
			green: ["#000", "#0f0"],
			blue: ["#000", "#00f"],
			hue: [
				"#f00", // 0, Red,       0°
				"#ff0", // 1, Yellow,   60°
				"#0f0", // 2, Green,   120°
				"#0ff", // 3, Cyan,    180°
				"#00f", // 4, Blue,    240°
				"#f0f", // 5, Magenta, 300°
				"#f00",
			], // 6, Red,     360°
			val: ["#000", "#fff"],
			kelvin: ["#fff", "#000"],
			cyan: ["#000", "#0ff"],
			yellow: ["#000", "#ff0"],
			magenta: ["#000", "#f0f"],
		};

		this.type = "rgb";
		this.accuracy = 16;
		this.plotStyle = "continuous";
		this.plotColors = "flat";
		this.plotFill = true;
		// this.histType = "rgb";
		this.discreetWidth = Math.round(this.histCanvas.width / 255);
	}

	setSourceCtx(context) {
		this.sourceCtx = context;
	}

	updateGradients() {
		this.gradients = {
			red: this.histCtx.createLinearGradient(0, 0, this.sourceCtx.canvas.width, 0),
			green: this.histCtx.createLinearGradient(0, 0, this.sourceCtx.canvas.width, 0),
			blue: this.histCtx.createLinearGradient(0, 0, this.sourceCtx.canvas.width, 0),
			hue: this.histCtx.createLinearGradient(0, 0, this.sourceCtx.canvas.width, 0),
			val: this.histCtx.createLinearGradient(0, 0, this.sourceCtx.canvas.width, 0),
			// cyan: histCtx.createLinearGradient(0, 0, img.width, 0),
			// magenta: histCtx.createLinearGradient(0, 0, img.width, 0),
			// yellow: histCtx.createLinearGradient(0, 0, img.width, 0),
			// kelvin: histCtx.createLinearGradient(0, 0, img.width, 0),
		};

		let grad, color, i, n;
		for (grad in this.gradients) {
			color = this.colors[grad];
			grad = this.gradients[grad];

			for (i = 0, n = color.length; i < n; i++) {
				grad.addColorStop((i * 1) / (n - 1), color[i]);
			}
		}
	}

	render() {
		let gl = this.sourceCtx;
		let width = lutify.imageWidth;
		let height = lutify.imageHeight;
		let pixels = new Uint8Array(width * height * 4);
		gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

		this.imgData = pixels;
		this.calcHist();
	}

	calcHist() {
		var chans = [[]],
			maxCount = 0,
			val,
			subtypes = [this.type];

		if (this.type === "rgb") {
			chans = [[], [], []];
			subtypes = ["red", "green", "blue"];
		} else if (this.type === "cmyk") {
			chans = [[], [], [], []];
			subtypes = ["cyan", "magenta", "yellow", "kelvin"];
		}

		var step = parseInt(this.accuracy);
		if (isNaN(step) || step < 1) {
			step = 1;
		} else if (step > 50) {
			step = 50;
		}
		this.accuracy = step;
		step *= 4;

		let imgDataLength = this.imgData.length;

		for (let i = 0, n = imgDataLength; i < n; i += step) {
			if (this.type === "rgb" || this.type === "red" || this.type === "green" || this.type === "blue") {
				val = [this.imgData[i], this.imgData[i + 1], this.imgData[i + 2]];
			} else if (
				this.type === "cmyk" ||
				this.type === "cyan" ||
				this.type === "magenta" ||
				this.type === "yellow" ||
				this.type === "kelvin"
			) {
				val = this.rgb2cmyk(this.imgData[i], this.imgData[i + 1], this.imgData[i + 2]);
			} else if (this.type === "hue" || this.type === "sat" || this.type === "val") {
				val = this.rgb2hsv(this.imgData[i], this.imgData[i + 1], this.imgData[i + 2]);
			}

			if (this.type === "red" || this.type === "hue" || this.type === "cyan") {
				val = [val[0]];
			} else if (this.type === "green" || this.type === "sat" || this.type === "magenta") {
				val = [val[1]];
			} else if (this.type === "blue" || this.type === "val" || this.type === "yellow") {
				val = [val[2]];
			} else if (this.type === "kelvin") {
				val = [val[3]];
			}

			for (var y = 0, m = val.length; y < m; y++) {
				if (val[y] in chans[y]) {
					chans[y][val[y]]++;
				} else {
					chans[y][val[y]] = 1;
				}

				if (chans[y][val[y]] > maxCount) {
					maxCount = chans[y][val[y]];
				}
			}
		}

		if (maxCount === 0) {
			return;
		}

		this.histCtx.clearRect(0, 0, this.histCanvas.width, this.histCanvas.height);

		if (this.plotFill && chans.length > 1) {
			this.histCtx.globalCompositeOperation = "lighter";
		}

		for (let i = 0, n = chans.length; i < n; i++) {
			this.drawHist(subtypes[i], chans[i], maxCount);
		}

		if (this.plotFill && chans.length > 1) {
			this.histCtx.globalCompositeOperation = "source-over";
		}
	}

	rgb2hsv(red, green, blue) {
		red /= 255;
		green /= 255;
		blue /= 255;

		var hue,
			sat,
			min = Math.min(red, green, blue),
			max = Math.max(red, green, blue),
			delta = max - min,
			val = max;

		// This is gray (red==green==blue)
		if (delta === 0) {
			hue = sat = 0;
		} else {
			sat = delta / max;

			if (max === red) {
				hue = (green - blue) / delta;
			} else if (max === green) {
				hue = (blue - red) / delta + 2;
			} else if (max === blue) {
				hue = (red - green) / delta + 4;
			}

			hue /= 6;
			if (hue < 0) {
				hue += 1;
			}
		}

		return [Math.round(hue * 255), Math.round(sat * 255), Math.round(val * 255)];
	}

	// Note that this is only an approximation of the CMYK color space. for proper
	// CMYK color space conversion one needs to implement support for color
	// profiles.
	// rgb2cmyk (red, green, blue) {
	//     var cyan    = 1 - red/255;
	//         magenta = 1 - green/255;
	//         yellow  = 1 - blue/255;
	//         black = Math.min(cyan, magenta, yellow, 1);

	//     if (black === 1) {
	//       cyan = magenta = yellow = 0;
	//     } else {
	//       var w = 1 - black;
	//       cyan    = (cyan    - black) / w;
	//       magenta = (magenta - black) / w;
	//       yellow  = (yellow  - black) / w;
	//     }

	//     return [Math.round(cyan*255), Math.round(magenta*255),
	//            Math.round(yellow*255), Math.round(black*255)];
	//   };

	drawHist(type, vals, maxCount) {
		let ctxStyle;

		if (this.plotFill || this.plotStyle === "discreet") {
			ctxStyle = "fillStyle";
			this.histCtx.strokeStyle = "#000";
		} else {
			ctxStyle = "strokeStyle";
		}

		if (this.plotColors === "flat") {
			if (type === "hue") {
				this.histCtx[ctxStyle] = this.gradients.hue;
			} else if (type in this.colors && type !== "val") {
				this.histCtx[ctxStyle] = this.colors[type][1];
			} else {
				this.histCtx[ctxStyle] = "#fff";
			}
		} else if (this.plotColors === "gradient") {
			if (type in this.gradients) {
				this.histCtx[ctxStyle] = this.gradients[type];
			} else {
				this.histCtx[ctxStyle] = "#fff";
			}
		} else if (this.plotColors === "none") {
			this.histCtx[ctxStyle] = "#fff";
		}

		if (this.plotStyle === "continuous") {
			this.histCtx.beginPath();
			this.histCtx.moveTo(0, this.histCanvas.height);
		}

		for (var x, y, i = 0; i <= 255; i++) {
			if (!(i in vals)) {
				continue;
			}

			y = Math.round((vals[i] / maxCount) * this.histCanvas.height);
			x = Math.round((i / 255) * this.histCanvas.width);

			if (this.plotStyle === "continuous") {
				this.histCtx.lineTo(x, this.histCanvas.height - y);
			} else if (this.plotStyle === "discreet") {
				if (this.plotFill) {
					this.histCtx.fillRect(x, this.histCanvas.height - y, this.discreetWidth, y);
				} else {
					this.histCtx.fillRect(x, this.histCanvas.height - y, this.discreetWidth, 2);
				}
			}
		}

		if (this.plotStyle === "continuous") {
			this.histCtx.lineTo(x, this.histCanvas.height);
			if (this.plotFill) {
				this.histCtx.fill();
			}
			this.histCtx.stroke();
			this.histCtx.closePath();
		}
	}
}
